import { formatiereEuro } from './euro.js';
import { GEWICHT_MAX_GRAMM, formatiereKg, gewichtPreisCent } from './gewicht.js';
import type { Belegposition } from './positionen.js';

/**
 * Mengen und Einzelpreise von Belegpositionen – EINE Umsetzung für Kern (Prüfung, Belegtext),
 * Server (Preisprüfung, Rechnung) und Kassen-App (Offline-Bon, Rechnung A4, Kundenanzeige).
 * Reines Modul ohne Abhängigkeiten außer gewicht.ts/euro.ts.
 *
 * - Stückposition (ohne `einheit`): Menge ist eine ganze Zahl ≥ 1, Betrag = Einzelpreis × Menge.
 * - Wiegeposition (`einheit: 'kg'`): Menge = gramm / 1000 (1 … 99.999 g, höchstens drei
 *   Nachkommastellen), Betrag = gewichtPreisCent(gramm, Kilopreis) – nie Kilopreis × kg mit
 *   Gleitkomma.
 */

/** Felder, die für Menge und Einzelpreis zählen. */
export type MengenAngabe = Pick<Belegposition, 'menge' | 'einheit' | 'einzelpreisCent'>;

/**
 * Ganze Gramm einer kg-Menge, wenn sie exakt `gramm / 1000` mit 1 … GEWICHT_MAX_GRAMM ist
 * (höchstens drei Nachkommastellen); sonst null. Keine Rundung – 0,7505 kg ist ungültig.
 */
export function grammAusKgMenge(menge: number): number | null {
  if (!Number.isFinite(menge)) {
    return null;
  }
  const gramm = Math.round(menge * 1000);
  return gramm >= 1 && gramm <= GEWICHT_MAX_GRAMM && gramm / 1000 === menge ? gramm : null;
}

/** Ganze Gramm einer Wiegeposition; null bei Stückpositionen oder ungültiger kg-Menge. */
export function grammDerPosition(p: Pick<MengenAngabe, 'menge' | 'einheit'>): number | null {
  return p.einheit === 'kg' ? grammAusKgMenge(p.menge) : null;
}

/** Stückmenge zulässig? Ganze Zahl ≥ 1 (Bruchmengen gibt es nur als kg). */
export function istStueckmenge(menge: number): boolean {
  return Number.isSafeInteger(menge) && menge >= 1;
}

/**
 * Betrag, den eine Position zum Einzelpreis `preisCent` haben muss: kg über
 * gewichtPreisCent(Gramm, Kilopreis), Stück als Einzelpreis × ganze Menge. null = Menge
 * ungültig (Bruchmenge bei Stück, ungültiges Gewicht) oder Preis nicht ganzzahlig.
 */
export function erwarteterZeilenbetrag(
  p: Pick<MengenAngabe, 'menge' | 'einheit'>,
  preisCent: number,
): number | null {
  if (!Number.isSafeInteger(preisCent)) {
    return null;
  }
  if (p.einheit === 'kg') {
    const gramm = grammAusKgMenge(p.menge);
    return gramm === null ? null : gewichtPreisCent(gramm, preisCent);
  }
  if (!istStueckmenge(p.menge)) {
    return null;
  }
  const betrag = preisCent * p.menge;
  return Number.isSafeInteger(betrag) ? betrag : null;
}

/** „0,750 kg × 5,99 €/kg“ (ohne Kilopreis nur „0,750 kg“); null für Stückpositionen. */
export function kgMengenText(p: MengenAngabe): string | null {
  const gramm = grammDerPosition(p);
  if (gramm === null) {
    return null;
  }
  const kg = formatiereKg(gramm);
  return p.einzelpreisCent === undefined ? kg : `${kg} × ${formatiereEuro(p.einzelpreisCent)} €/kg`;
}

/** Mengenspalte: „0,750 kg“ bei Wiegepositionen, sonst die Stückzahl. */
export function mengeSpalte(p: MengenAngabe): string {
  const gramm = grammDerPosition(p);
  return gramm === null ? String(p.menge) : formatiereKg(gramm);
}

/**
 * Einzelpreis in Cent für die Anzeige: bei Wiegepositionen der Kilopreis (nie aus dem
 * gerundeten Zeilenbetrag zurückgerechnet), sonst Betrag ÷ Menge (Cent gerundet).
 */
export function anzeigeEinzelpreisCent(p: MengenAngabe & { bruttoCent: number }): number {
  if (grammDerPosition(p) !== null && p.einzelpreisCent !== undefined) {
    return p.einzelpreisCent;
  }
  return Math.round(p.bruttoCent / (p.menge === 0 ? 1 : p.menge));
}

/** Einzelpreisspalte: „5,99/kg“ bei Wiegepositionen, sonst „0,45“. */
export function einzelpreisSpalte(p: MengenAngabe & { bruttoCent: number }): string {
  const text = formatiereEuro(anzeigeEinzelpreisCent(p));
  return grammDerPosition(p) !== null && p.einzelpreisCent !== undefined ? `${text}/kg` : text;
}
